import {
  CheckIcon,
  ColorInput,
  ColorSwatch,
  Combobox,
  Group,
  Loader,
  Pill,
  PillsInput,
  useCombobox,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useLanguage } from "../../stores/LanguageStore";
import {
  getTagBackgroundColor,
  getTagsSwatches,
} from "../../functions/utilFunctions";

const TagsSelector = ({
  data,
  value,
  setValue,
  label,
  placeholder,
  leftSection,
  createFunction,
  createLabel,
  createLabelPlaceholder,
  onDropdownOpen,
  onNewValueCreated,
  loading,
  created_field_id,
  autoFocus,
  ...props
}) => {
  const lang = useLanguage((s) => s.language);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => {
      combobox.updateSelectedOptionIndex("active");
      onDropdownOpen?.();
    },
  });

  const [search, setSearch] = useState("");

  const exactOptionMatch = data?.some?.((item) => item === search);

  const [creating, setCreating] = useState(false);
  const handleNewValue = () => {
    setCreating(true);

    const color = document.getElementById("new-tag-color")?.value || undefined;
    createFunction({
      name: search,
      color: color ? color.substring(1) : undefined,
    })
      .then((response) => {
        onNewValueCreated?.();
        setValue((current) => [
          ...current,
          { id: response.data[created_field_id], name: search },
        ]);
        setSearch("");
      })
      .finally(() => setCreating(false));
  };

  const handleValueSelect = (val) => {
    if (val === "$create") {
      handleNewValue();
    } else {
      setValue((current) =>
        current.find((x) => x.id === val.id)
          ? current.filter((v) => v.id !== val.id)
          : [...current, val]
      );
      setSearch("");
    }
    // combobox.closeDropdown();
  };

  const handleValueRemove = (val) =>
    setValue((current) => current.filter((v) => v.id !== val.id));

  const values = value.map((item) => (
    <Pill
      key={item.id}
      withRemoveButton
      onRemove={() => handleValueRemove(item)}
      bg={getTagBackgroundColor(item.name)}
    >
      {item.name}
    </Pill>
  ));

  const options = data
    .filter((item) =>
      item.name.toLowerCase().includes(search.trim().toLowerCase())
    )
    .map((item) => (
      <Combobox.Option
        value={item}
        key={item.id}
        active={value.find((x) => x.id === item.id)}
        w={"fit-content"}
        bg={getTagBackgroundColor(item.name)}
        style={{ borderRadius: 12 }}
      >
        <Group gap="sm">
          {value.find((x) => x.id === item.id) ? <CheckIcon size={12} /> : null}
          <span>{item.name}</span>
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      withinPortal={true}
      zIndex={1400}
      shadow="sm"
    >
      <Combobox.DropdownTarget>
        <PillsInput
          onClick={() => combobox.openDropdown()}
          label={label}
          leftSection={leftSection}
          rightSection={
            loading ? <Loader size={"xs"} type="dots" /> : <Combobox.Chevron />
          }
          rightSectionPointerEvents="none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && search && options.length === 0) {
              document.getElementById("create-tag").click();
              e.stopPropagation()
              e.preventDefault()
            }
          }}
          {...props}
        >
          <Pill.Group>
            {values}

            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                autoFocus={autoFocus}
                value={search}
                placeholder={placeholder}
                onChange={(event) => {
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && search.length === 0) {
                    event.preventDefault();
                    handleValueRemove(value[value.length - 1]);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options
          mah={172}
          style={{
            overflowY: "auto",
            flexWrap: "wrap",
            display: "flex",
            gap: 8,
          }}
        >
          <Combobox.Option
            value="$create"
            component={Group}
            gap={6}
            disabled={creating || !search}
            w={"100%"}
            id="create-tag"
          >
            {creating ? (
              <Loader size={14} type="dots" />
            ) : (
              <IconPlus size={14} />
            )}
            <span>
              {createLabel} <b> {search || createLabelPlaceholder}</b>
            </span>

            {options.length === 0 && (
              <Group
                onClick={(e) => {
                  e.stopPropagation();
                }}
                w={"100%"}
              >
                <ColorInput
                  placeholder={lang.prm.status.color}
                  format="hex"
                  w={"100%"}
                  popoverProps={{
                    zIndex: 1500,
                    withinPortal: false,
                    opened: true,
                    keepMounted: true,
                  }}
                  id="new-tag-color"
                  closeOnColorSwatchClick={true}
                  swatches={getTagsSwatches()}
                  withPicker={false}
                  styles={{
                    dropdown: {
                      width: "100%",
                      display: "flex",
                      height: "auto",
                      position: "static",
                      justifyContent: "center",
                    },
                    swatches: {
                      position: "static",
                      width: "100%",
                    },
                    wrapper: {
                      width: "100%",
                    },
                  }}
                />
              </Group>
            )}
          </Combobox.Option>

          {options}

          {exactOptionMatch &&
            search.trim().length > 0 &&
            options.length === 0 && (
              <Combobox.Empty>{lang.global.no_element_found}</Combobox.Empty>
            )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default TagsSelector;
